import { useMedplum } from "@medplum/react-hooks";
import { AgentChat, chatThreadQueries } from "@repo/agent-chat";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect } from "react";
import { setActiveThreadId, useActiveThreadId } from "@/features/chat/stores/use-active-thread";
import { AI_ENGINE_URL } from "@/shared/lib/ai-engine-url";
import { getChatRouteContext } from "./chat-route-context";

export interface AgentChatRootProps {
  children: ReactNode;
  onAfterSubmit?: () => void;
  /**
   * Controlled-thread seam (MIN-239/240): when supplied, this thread is shown
   * regardless of the store's `activeThreadId`. `null` (MIN-240's `/chat`
   * index) forces the empty new-chat composer regardless of any thread
   * already active in the store. When omitted, the compound falls back to
   * the store — today's gutter-panel behavior, unchanged.
   */
  threadId?: string | null;
  /**
   * Fires when a controlled caller's thread-changing actions (new chat, delete
   * down to zero remaining threads) pick a new thread. `null` means "no thread
   * remains" — mirrors `setActiveThreadId`'s own signature, which the
   * uncontrolled path passes straight through.
   */
  onThreadChange?: (threadId: string | null) => void;
}

/**
 * MIN-293 — thin app wrapper over `@repo/agent-chat`'s (now purely controlled)
 * `AgentChat.Root`. This is the one place `agentActiveThreadStore`'s
 * auto-pick-most-recent lives: the gutter panel's "never empty" rule is a
 * policy only one of six surfaces wants, and `/chat` actively forbids it —
 * that's app policy, not chat-domain knowledge, so it stays here rather than
 * in the package. Everything else (submit/stop/draft, thread create/delete,
 * the render registry) is the package's; this wrapper declares none of it.
 */
export function AgentChatRoot({ children, onAfterSubmit, threadId, onThreadChange }: AgentChatRootProps) {
  const medplum = useMedplum();
  const storedThreadId = useActiveThreadId();
  const { data: threads } = useQuery(chatThreadQueries.list(medplum, AI_ENGINE_URL));
  const isControlled = threadId !== undefined;

  // Auto-pick most recent when nothing's active — gives the panel a
  // non-empty default. Only applies when uncontrolled; a controlled caller
  // (including `/chat`'s explicit `threadId={null}`) owns thread selection.
  useEffect(() => {
    if (!isControlled && storedThreadId === null && threads && threads.length > 0) {
      setActiveThreadId(threads[0].id);
    }
  }, [isControlled, storedThreadId, threads]);

  // The caller's thread wins over the stored one outright — including `null`,
  // which forces the empty composer regardless of what the store holds.
  const resolvedThreadId = threadId !== undefined ? threadId : storedThreadId;

  // Thread-changing actions report through here: uncontrolled writes the
  // shared store (today's behavior); controlled reports to the caller
  // instead, so the caller (e.g. the route) can decide what to do — navigate,
  // etc.
  const handleThreadChange = useCallback(
    (id: string | null) => {
      if (isControlled) onThreadChange?.(id);
      else setActiveThreadId(id);
    },
    [isControlled, onThreadChange],
  );

  return (
    <AgentChat.Root
      auth={medplum}
      baseUrl={AI_ENGINE_URL}
      getRouteContext={getChatRouteContext}
      onAfterSubmit={onAfterSubmit}
      onThreadChange={handleThreadChange}
      threadId={resolvedThreadId}
    >
      {children}
    </AgentChat.Root>
  );
}
